/**
 * "Is it going to rain in the coming hours?" — derived from the hourly forecast.
 *
 * A pure function so it is unit-testable and runs the same on the server and in
 * the card. It returns **structured** data, never a sentence: display strings are
 * Dutch and live in the client (`web/src/weather/*`), like the WMO labels — the
 * same split as everywhere else (enum-shaped facts here, words there).
 *
 * The window is the `hourly` array the server already anchors to the current hour
 * (its first entry is "now"), so this scans forward without needing a clock.
 */
/**
 * A meaningful amount of rain forecast for an hour, in mm. Below this reads as
 * "dry". This keys off the forecast **amount**, not the probability: Open-Meteo
 * hands out high precipitation_probability freely (96% on an hour it forecasts
 * 0.0 mm for is common), so a probability trigger would announce rain on hours
 * nothing is expected to fall. The probability rides along as the shown
 * confidence of the hour that does clear the bar.
 *
 * Exported so the dashboard's hourly strip draws "dry" on the exact same bar the
 * summary does: the strip shows the amount, not that misleading 96%, so the two
 * can never disagree about whether a given hour is wet.
 */
export const WET_MM = 0.2;
const isWet = (h) => h.precipitation >= WET_MM;
/**
 * @param currentPrecip  `current.precipitation` — mm falling right now.
 * @param hourly         the coming hours, first entry = the current hour.
 */
export function rainOutlook(currentPrecip, hourly) {
    const first = hourly[0];
    // "Now" keys off the live measurement or the current hour's forecast *amount* —
    // never the probability branch, so a dry-but-likely hour is not called rain.
    const rainingNow = currentPrecip > 0 || (first ? first.precipitation >= WET_MM : false);
    if (rainingNow) {
        return { state: 'raining', probability: first?.precipitation_probability ?? 100 };
    }
    // Scan the future hours (skip the current one) for the first wet boundary.
    for (let i = 1; i < hourly.length; i++) {
        const h = hourly[i];
        if (isWet(h)) {
            return { state: 'starts', time: h.time, probability: h.precipitation_probability };
        }
    }
    return { state: 'dry' };
}
/**
 * The next moment rain *starts* — the first dry→wet edge at or after `now`.
 *
 * This is the trigger for the "rain in ~30 minutes" push (the sweep in
 * `api/src/weather/notify.ts`), and it is deliberately finer-grained than
 * `rainOutlook`: that one answers the dashboard's hourly "will it rain today?"
 * from the hourly forecast, this one drives an alert with a clock promise from
 * the 15-minutely forecast, so "30 minutes" means 30 minutes and not "sometime
 * in the coming hour".
 *
 * Pure and timezone-agnostic: it works on **absolute instants** — each bucket's
 * `time` is a full ISO instant and `nowMs` is epoch milliseconds — so the caller
 * resolves Open-Meteo's local wall-clock buckets to real times first, and this
 * never has to know the household's zone. Buckets are ascending, as Open-Meteo
 * returns them.
 *
 * A "start" is a transition, not merely a wet bucket: a bucket counts only when
 * the one before it is dry. That is what fires the alert once per shower rather
 * than for every 15-minute slot a long spell of rain occupies — and it means
 * rain already falling now yields the *next* shower, not this one (there is
 * nothing to pre-warn about weather that has already arrived).
 *
 * @param wet  the amount at or above which a bucket counts as rain. Defaults to
 *             `WET_MM` (mm, as the hourly forecast reports), but the radar
 *             nowcast that actually drives the push measures in mm/h, so its
 *             caller passes its own bar — hence the parameter rather than a
 *             hard-coded constant.
 * @returns the starting bucket's `time`, or null if no rain begins in the window.
 */
export function nextRainStart(buckets, nowMs, wet = WET_MM) {
    for (let i = 1; i < buckets.length; i++) {
        if (buckets[i].precipitation < wet)
            continue; // this slot is dry
        if (buckets[i - 1].precipitation >= wet)
            continue; // already raining — not a start
        const atMs = Date.parse(buckets[i].time);
        if (Number.isNaN(atMs) || atMs < nowMs)
            continue; // a start already in the past
        return buckets[i].time;
    }
    return null;
}
export function radarOutlook(buckets, nowMs, wet = 0.1) {
    // The slot covering "now": the last bucket at or before it. Buckets ascend.
    let cur = -1;
    for (let i = 0; i < buckets.length; i++) {
        if (Date.parse(buckets[i].time) <= nowMs)
            cur = i;
        else
            break;
    }
    if (cur >= 0 && buckets[cur].precipitation >= wet)
        return { state: 'raining' };
    const start = nextRainStart(buckets, nowMs, wet);
    if (start)
        return { state: 'starts', time: start };
    // The whole window is still ahead of us and its first bucket is already wet —
    // no earlier dry slot for nextRainStart to pivot on, so treat it as the start.
    const first = buckets[0];
    if (cur < 0 && first && first.precipitation >= wet && Date.parse(first.time) >= nowMs) {
        return { state: 'starts', time: first.time };
    }
    return { state: 'dry' };
}
//# sourceMappingURL=weather.js.map